import { ColumnDef } from "@tanstack/react-table";
import { CardConditionEnum, FinishEnum } from "modules/cards/model/card";
import ConditionChip from "modules/common/components/Table/ConditionChip";
import LanguageFlag from "modules/common/components/Table/LanguageFlag";
import SetTooltip from "modules/common/components/Table/SetTooltip";
import React from "react";
import RecentTradesContactButton from "modules/tradeLists/components/RecentTrades/RecentTradesContactButton";
import { TradeItemListingLink } from "modules/tradeLists/components/RecentTrades/TradeItemListingLink";
import { UserHoverCard } from "modules/common/components/navigation/UserHoverCard";
import { CardPrimaryPrice } from "modules/cards/components/CardPrimaryPrice";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";
import { FetchCardDataNameCell } from "modules/collections/details/components/FetchCardDataNameCell";
import { useCardDetailsModal } from "modules/cards/hooks/useCardDetailsModal";
import { CityPagedItem } from "modules/city/model/cityCards";
import { RelativeTime } from "modules/common/components/ui/RelativeTime";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import { StoreDropdownButton } from "modules/city/components/client/StoreDropdownButton";
import { primaryDisplayFinish } from "modules/cards/model/finishBehavior";
import { CardFinishIndicator } from "modules/common/components/CardSpecOverlay";
import { CityMatchScopeBadge } from "modules/city/components/client/CityMatchScopeBadge";
import { ActiveProposalBadge } from "modules/tradeLists/components/ActiveProposalBadge";

export function useCityMatchedCardColumns(): ColumnDef<CityPagedItem>[] {
  const { userId, isPending: isPendingUser } = useAppUser();
  const { handleOpenCardDetailsByDataId } = useCardDetailsModal();

  const baseColumns: ColumnDef<CityPagedItem>[] = [
    {
      accessorKey: "quantity",
      size: 36,
      header: () => <div className="text-center">Qty</div>,
      cell: (info) => (
        <div className="text-base-content/70 text-center text-sm">
          {info.getValue() as number}
        </div>
      ),
    },
    {
      accessorKey: "name",
      header: "Card",
      cell: (info) => {
        const item = info.row.original;
        const finish = primaryDisplayFinish(item.finishes);
        return (
          <div className={"flex flex-row gap-1"}>
            <CardFinishIndicator finish={finish} />
            <FetchCardDataNameCell
              onClick={() =>
                handleOpenCardDetailsByDataId(item.dataId, item.game, finish)
              }
              basicCard={item}
              game={item.game}
              name={info.getValue() as string}
            />
            <ActiveProposalBadge item={item} className="shrink-0" />
          </div>
        );
      },
    },
    {
      id: "stores",
      header: () => <div className="text-center">Stores</div>,
      cell: (info) => {
        const item = info.row.original;
        const stores = item.stores || [];

        if (stores.length === 0) {
          return <div className="text-center text-sm">—</div>;
        }

        return (
          <div className="flex justify-center">
            <StoreDropdownButton stores={stores} size="sm" item={item} />
          </div>
        );
      },
    },
    {
      id: "matchScope",
      size: 112,
      header: () => <div className="text-center">Reach</div>,
      cell: (info) => (
        <div className="flex justify-center">
          <CityMatchScopeBadge scope={info.row.original.matchScope} />
        </div>
      ),
    },
    {
      accessorKey: "setCode",
      size: 60,
      header: () => <div className="text-center">Set</div>,
      cell: (info) => (
        <div className="flex items-center justify-center">
          <SetTooltip item={info.row.original} />
        </div>
      ),
    },
    {
      id: "price",
      size: 80,
      header: () => <div className="text-center">Price</div>,
      cell: (info) => (
        <div className="text-base-content text-center text-sm">
          <CardPrimaryPrice card={info.row.original} emptyFallback="—" />
        </div>
      ),
    },
    {
      accessorKey: "language",
      size: 60,
      header: () => <div className="text-center">Lang</div>,
      cell: (info) => (
        <div className="flex justify-center">
          <LanguageFlag language={info.getValue() as string} />
        </div>
      ),
    },
    {
      accessorKey: "condition",
      size: 70,
      header: () => <div className="text-center">Cond</div>,
      cell: (info) => (
        <div className="flex justify-center">
          <ConditionChip
            condition={info.getValue() as CardConditionEnum}
            showMinimum={info.row.original.list?.type === TradeListType.WANTS}
          />
        </div>
      ),
    },
    {
      accessorKey: "updatedAt",
      size: 130,
      header: "Updated",
      cell: (info) => (
        <RelativeTime
          date={info.getValue() as Date}
          className="block text-xs md:text-sm"
        />
      ),
    },
  ];

  // Always show user columns for city matched view
  baseColumns.push({
    id: "user",
    header: () => <div className="text-center">User</div>,
    cell: (info) => {
      const item = info.row.original;
      if (!item.user) {
        return (
          <div className="text-base-content/70 text-center text-sm">—</div>
        );
      }
      return (
        <div className="flex items-center justify-center gap-2">
          <UserHoverCard
            size="xs"
            userId={item.user.id}
            username={item.user.username}
            avatar={item.user.preferredAvatar}
            maxUsernameLength={12}
            usernameClassName="text-sm font-medium"
            activityStatus={item.activityStatus}
          />
        </div>
      );
    },
  });

  baseColumns.push({
    id: "actions",
    size: 80,
    header: () => <div className="text-center">Contact</div>,
    cell: (info) => {
      const item = info.row.original;
      if (!item.user) {
        return (
          <div className="text-base-content/70 text-center text-sm">—</div>
        );
      }
      const isCurrentUserItem = item.user.id === userId;

      if (isCurrentUserItem) {
        return (
          <div className="text-center">
            <TradeItemListingLink item={item} size={"small"} />
          </div>
        );
      }

      return (
        <div className="text-center">
          <RecentTradesContactButton item={item} size={"small"} fromMatch />
        </div>
      );
    },
  });

  return baseColumns;
}
